import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import {
  isNative,
  platform,
  hideSplash,
  setStatusBarLight,
  listenForDeepLinks,
  registerForPush,
  onNetworkChange,
  getNetworkStatus,
} from '@/lib/native';

/**
 * <NativeBridge /> — mount-once component that wires all Capacitor plugins
 * into the React app. Renders an offline banner when disconnected.
 *
 * Place inside <Layout /> above <Outlet />.
 */
export default function NativeBridge() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [offline, setOffline] = useState(false);
  const pushDisposeRef = useRef(null);

  // ---------- Bootstrap once ----------
  useEffect(() => {
    if (!isNative) {
      // Web: still show offline banner
      const off = onNetworkChange(({ connected }) => setOffline(!connected));
      getNetworkStatus().then(({ connected }) => setOffline(!connected));
      return () => off?.();
    }

    // Native: hide splash + configure status bar
    hideSplash();
    setStatusBarLight();

    // ---------- Deep-links ----------
    const disposeLinks = listenForDeepLinks((pathAndQuery) => {
      // SEC-post-iter13: validate the path before feeding it to React Router.
      // The OS hands us URLs from other apps — a malicious deep link could
      // contain path traversal (`/../`), a `javascript:` scheme, or a
      // protocol-relative URL that navigates off-app.
      if (typeof pathAndQuery !== 'string') return;
      // Must start with a single leading slash — reject `//`, `\\`, empty.
      if (!/^\/[^/\\]/.test(pathAndQuery)) return;
      // Reject traversal + control chars.
      if (pathAndQuery.includes('/../') || pathAndQuery.includes('/..\\')) return;
      if (/[\u0000-\u001f]/.test(pathAndQuery)) return;
      // Cap length.
      if (pathAndQuery.length > 512) return;
      try { nav(pathAndQuery); } catch { /* invalid route */ }
    });

    // ---------- Offline banner ----------
    const offCb = onNetworkChange(({ connected }) => setOffline(!connected));
    getNetworkStatus().then(({ connected }) => setOffline(!connected));

    // ---------- Push registration ----------
    // Only prompt the user on first launch of the native app. We check a flag
    // in localStorage so subsequent app launches don't nag the user again.
    const ASKED = 'symposia.push.asked';
    if (!localStorage.getItem(ASKED)) {
      localStorage.setItem(ASKED, '1');
      registerForPush({
        onToken: async (token) => {
          try {
            await api.post('/me/push-token', { token, platform });
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('failed to send push token', e?.message);
          }
        },
        onReceive: (notif) => {
          // Foreground receipt — show a subtle toast
          if (notif?.title || notif?.body) {
            toast(notif.title || t('common.notification'), { description: notif.body });
          }
        },
        onAction: (event) => {
          // User tapped the notification — jump to the linked screen.
          // Push-notification data is server-controlled but validate anyway
          // as defence in depth.
          const data = event?.notification?.data || {};
          const path = data.path || data.url;
          if (
            typeof path === 'string' &&
            /^\/[^/\\]/.test(path) &&
            !path.includes('/../') &&
            !/[\u0000-\u001f]/.test(path) &&
            path.length <= 512
          ) {
            nav(path);
          }
        },
      }).then((res) => { pushDisposeRef.current = res.dispose; });
    }

    return () => {
      disposeLinks?.();
      offCb?.();
      pushDisposeRef.current?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!offline) return null;

  return (
    <div
      data-testid="offline-banner"
      role="status"
      className="fixed top-0 inset-x-0 z-[100] bg-zinc-950 text-white text-center py-2 text-xs font-mono uppercase tracking-[0.12em]"
    >
      {t('common.offline')}
    </div>
  );
}
